//frontend/src/components/AdminRequests/Chat/index.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";

import ChatHeader from "./components/ChatHeader";
import MessageList from "./components/MessageList";
import MessageInput from "./components/MessageInput";

import {
  fetchMessages,
  getJoinedMembers,
  getProfile,
  sendReaction,
  sendText,
  syncMessages,
  uploadAndSendFile,
} from "./matrixClient";

import "./Chat.css";

// ─── Кэш истории в localStorage ──────────────────────────────
const CACHE_PREFIX = "admin-chat:v1:";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const cacheKey = (roomId) => `${CACHE_PREFIX}${roomId}`;

function readCache(roomId) {
  try {
    const raw = localStorage.getItem(cacheKey(roomId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !parsed.savedAt ||
      parsed.savedAt + CACHE_TTL_MS < Date.now()
    ) {
      localStorage.removeItem(cacheKey(roomId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(roomId, { events, nextBatch, hasMore, profiles }) {
  try {
    const cacheable = events.filter((ev) => !ev.__local && !ev.__localUrl);
    localStorage.setItem(
      cacheKey(roomId),
      JSON.stringify({
        savedAt: Date.now(),
        events: cacheable,
        nextBatch,
        hasMore,
        profiles,
      })
    );
  } catch {
    // quota / serialization error — игнорируем
  }
}

// Слияние событий: дедуп по event_id, сортировка по origin_server_ts.
// incoming считается «более свежим» и перетирает existing по тому же event_id.
function mergeEvents(existing, incoming) {
  const byId = new Map();
  for (const ev of existing) byId.set(ev.event_id, ev);
  for (const ev of incoming) byId.set(ev.event_id, ev);
  const merged = [...byId.values()];
  merged.sort(
    (a, b) => (a.origin_server_ts || 0) - (b.origin_server_ts || 0)
  );
  return merged;
}

export default function AdminRequestChat({
  matrixRoomId,
  matrixUserId,
  accessToken,
}) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextBatch, setNextBatch] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  const enabled = Boolean(matrixRoomId && matrixUserId && accessToken);

  // Учёт blob-URL для optimistic-отображения файлов
  const blobUrlsRef = useRef(new Set());
  const registerBlobUrl = (url) => {
    blobUrlsRef.current.add(url);
    return url;
  };
  const revokeBlobUrl = (url) => {
    if (blobUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      blobUrlsRef.current.delete(url);
    }
  };

  useEffect(() => {
    return () => {
      for (const url of blobUrlsRef.current) URL.revokeObjectURL(url);
      blobUrlsRef.current.clear();
    };
  }, []);

  // Токен /sync — сохраняется при закрытии/открытии виджета
  // (компонент не размонтируется, пока жива страница).
  const syncTokenRef = useRef(null);
  // Актуальный profiles для sync-loop (избегаем stale closure).
  const profilesRef = useRef(profiles);
  useEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  // ── Загрузка профилей ──────────────────────────────────────
  const loadProfiles = useCallback(async () => {
    if (!enabled) return null;
    try {
      const members = await getJoinedMembers({
        accessToken,
        roomId: matrixRoomId,
      });
      const next = {};
      await Promise.all(
        Object.keys(members).map(async (userId) => {
          try {
            const p = await getProfile({ accessToken, userId });
            next[userId] = {
              displayname: p.displayname || null,
              avatar_url: p.avatar_url || null,
            };
          } catch {
            next[userId] = { displayname: null, avatar_url: null };
          }
        })
      );
      setProfiles(next);
      return next;
    } catch {
      return null;
    }
  }, [enabled, accessToken, matrixRoomId]);

  // ── Начальная загрузка (кэш + сервер) ──────────────────────
  const loadInitial = useCallback(
    async ({ background = false, skipCache = false } = {}) => {
      if (!enabled) return;

      if (!skipCache) {
        const cached = readCache(matrixRoomId);
        if (cached?.events?.length) {
          setEvents(cached.events);
          setNextBatch(cached.nextBatch || null);
          setHasMore(Boolean(cached.nextBatch));
          if (cached.profiles) setProfiles(cached.profiles);
        }
      }

      if (background) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const { events: list, end } = await fetchMessages({
          accessToken,
          roomId: matrixRoomId,
          limit: 50,
        });

        // Мерджим: сохраняем кэш + optimistic, поверх — серверные.
        setEvents((prev) => mergeEvents(prev, list));

        setNextBatch(end);
        setHasMore(Boolean(end));

        const freshProfiles = await loadProfiles();
        writeCache(matrixRoomId, {
          events: list,
          nextBatch: end,
          hasMore: Boolean(end),
          profiles: freshProfiles || profilesRef.current,
        });
      } catch (e) {
        if (!background) setError(e.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [enabled, accessToken, matrixRoomId, loadProfiles]
  );

  // Автозагрузка при открытии
  useEffect(() => {
    if (!open) return;
    if (events.length > 0) return;

    const cached = readCache(matrixRoomId);
    if (cached?.events?.length) {
      setEvents(cached.events);
      setNextBatch(cached.nextBatch || null);
      setHasMore(Boolean(cached.nextBatch));
      if (cached.profiles) setProfiles(cached.profiles);
      loadInitial({ background: true, skipCache: true });
    } else {
      loadInitial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matrixRoomId]);

  // Автосохранение кэша
  useEffect(() => {
    if (!enabled || events.length === 0) return;
    writeCache(matrixRoomId, { events, nextBatch, hasMore, profiles });
  }, [enabled, matrixRoomId, events, nextBatch, hasMore, profiles]);

  // ── LIVE-SYNC: long-polling /sync пока виджет открыт ───────
  useEffect(() => {
    if (!open || !enabled) return;

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      while (!cancelled) {
        try {
          const { events: newEvents, nextBatch: nb } = await syncMessages({
            accessToken,
            roomId: matrixRoomId,
            since: syncTokenRef.current,
            timeout: 30000,
            signal: controller.signal,
          });

          if (cancelled) return;
          if (nb) syncTokenRef.current = nb;

          if (newEvents.length > 0) {
            setEvents((prev) => {
              const existing = new Set(prev.map((e) => e.event_id));
              const fresh = newEvents.filter(
                (e) => !existing.has(e.event_id)
              );
              if (fresh.length === 0) return prev;
              return mergeEvents(prev, fresh);
            });

            // Профили для новых отправителей
            const missing = new Set();
            for (const ev of newEvents) {
              if (ev.sender && !profilesRef.current[ev.sender]) {
                missing.add(ev.sender);
              }
            }
            if (missing.size > 0) {
              const updates = {};
              await Promise.all(
                [...missing].map(async (userId) => {
                  try {
                    const p = await getProfile({ accessToken, userId });
                    updates[userId] = {
                      displayname: p.displayname || null,
                      avatar_url: p.avatar_url || null,
                    };
                  } catch {
                    updates[userId] = {
                      displayname: null,
                      avatar_url: null,
                    };
                  }
                })
              );
              if (!cancelled) {
                setProfiles((prev) => ({ ...prev, ...updates }));
              }
            }
          }
        } catch (e) {
          if (e.name === "AbortError" || cancelled) return;
          // network / server error — короткая пауза и повтор
          console.warn("sync error:", e.message);
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, enabled, accessToken, matrixRoomId]);

  // ── Догрузка старых сообщений ──────────────────────────────
  const loadMore = async () => {
    if (!enabled || !nextBatch || loadingMore) return;
    setLoadingMore(true);
    try {
      const { events: older, end } = await fetchMessages({
        accessToken,
        roomId: matrixRoomId,
        from: nextBatch,
        limit: 50,
      });
      setEvents((prev) => mergeEvents(older, prev));
      setNextBatch(end);
      setHasMore(Boolean(end));
    } catch {
      // тихо
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Отправка текста ────────────────────────────────────────
  const handleSendText = async (text) => {
    const replyId = replyTo?.event_id || null;
    setReplyTo(null);

    const tempId = `local_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    const optimistic = {
      event_id: tempId,
      sender: matrixUserId,
      type: "m.room.message",
      content: {
        msgtype: "m.text",
        body: text,
        ...(replyId
          ? { "m.relates_to": { "m.in_reply_to": { event_id: replyId } } }
          : {}),
      },
      origin_server_ts: Date.now(),
      __local: true,
    };
    setEvents((prev) => [...prev, optimistic]);

    try {
      const res = await sendText({
        accessToken,
        roomId: matrixRoomId,
        body: text,
        replyTo: replyId,
      });
      // Убираем возможный дубль, если sync успел добавить реальное событие
      setEvents((prev) => {
        const filtered = prev.filter(
          (ev) => ev.event_id !== res.event_id
        );
        return filtered.map((ev) =>
          ev.event_id === tempId
            ? { ...ev, event_id: res.event_id, __local: false }
            : ev
        );
      });
    } catch (e) {
      setEvents((prev) => prev.filter((ev) => ev.event_id !== tempId));
      alert(e.message);
    }
  };

  // ── Отправка файла ─────────────────────────────────────────
  const handleSendFile = async (file, msgtype) => {
    const tempId = `local_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    const localUrl = registerBlobUrl(URL.createObjectURL(file));

    const info = {
      mimetype: file.type || "application/octet-stream",
      size: file.size,
    };

    if (msgtype === "m.image") {
      try {
        const dims = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () =>
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve(null);
          img.src = localUrl;
        });
        if (dims) {
          info.width = dims.width;
          info.height = dims.height;
        }
      } catch {
        // ignore
      }
    }

    const optimistic = {
      event_id: tempId,
      sender: matrixUserId,
      type: "m.room.message",
      content: {
        msgtype,
        body: file.name,
        url: localUrl,
        info,
      },
      origin_server_ts: Date.now(),
      __local: true,
      __localUrl: localUrl,
    };

    setEvents((prev) => [...prev, optimistic]);

    try {
      const res = await uploadAndSendFile({
        accessToken,
        roomId: matrixRoomId,
        file,
        msgtype,
      });
      setEvents((prev) => {
        const filtered = prev.filter(
          (ev) => ev.event_id !== res.event_id
        );
        return filtered.map((ev) =>
          ev.event_id === tempId
            ? { ...ev, event_id: res.event_id, __local: false }
            : ev
        );
      });
    } catch (e) {
      setEvents((prev) => prev.filter((ev) => ev.event_id !== tempId));
      revokeBlobUrl(localUrl);
      alert(e.message);
    }
  };

  const handleSendAudio = async (blob) => {
    const file = new File([blob], `voice_${Date.now()}.webm`, {
      type: blob.type || "audio/webm",
    });
    await handleSendFile(file, "m.audio");
  };

  // ── Реакции ────────────────────────────────────────────────
  const handleReact = async (event, emoji) => {
    const tempId = `local_reaction_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    const optimistic = {
      event_id: tempId,
      sender: matrixUserId,
      type: "m.reaction",
      content: {
        "m.relates_to": {
          rel_type: "m.annotation",
          event_id: event.event_id,
          key: emoji,
        },
      },
      origin_server_ts: Date.now(),
      __local: true,
    };
    setEvents((prev) => [...prev, optimistic]);

    try {
      const res = await sendReaction({
        accessToken,
        roomId: matrixRoomId,
        eventId: event.event_id,
        emoji,
      });
      if (res?.event_id) {
        setEvents((prev) => {
          const filtered = prev.filter(
            (ev) => ev.event_id !== res.event_id
          );
          return filtered.map((ev) =>
            ev.event_id === tempId
              ? { ...ev, event_id: res.event_id, __local: false }
              : ev
          );
        });
      }
    } catch (e) {
      setEvents((prev) => prev.filter((ev) => ev.event_id !== tempId));
      alert(e.message);
    }
  };

  const handleReply = (event) => {
    const profile = profiles[event.sender] || {};
    setReplyTo({
      event_id: event.event_id,
      senderName:
        profile.displayname ||
        event.sender.split(":")[0].replace("@", ""),
      preview: event.content?.body?.slice(0, 120) || "(вложение)",
    });
  };

  return (
    <div className="chat-widget">
      <button
        type="button"
        className="chat-widget__toggle"
        onClick={() => setOpen((v) => !v)}
        title="Чат с администратором"
      >
        {open ? "×" : "💬"}
      </button>

      {open && (
        <div className="chat-widget__panel">
          <ChatHeader roomId={matrixRoomId} onClose={() => setOpen(false)} />

          {!enabled ? (
            <div className="chat-widget__empty">
              Matrix не настроен для этой заявки.
            </div>
          ) : loading && events.length === 0 ? (
            <div className="chat-widget__empty">Загрузка сообщений…</div>
          ) : error && events.length === 0 ? (
            <div className="chat-widget__empty">
              Ошибка: {error}
              <button type="button" onClick={() => loadInitial()}>
                Повторить
              </button>
            </div>
          ) : (
            <>
              {refreshing && events.length > 0 && (
                <div
                  className="chat-widget__refreshing"
                  style={{ fontSize: 11, padding: "4px 8px", opacity: 0.7 }}
                >
                  Обновление…
                </div>
              )}
              <MessageList
                events={events}
                meId={matrixUserId}
                accessToken={accessToken}
                profiles={profiles}
                onLoadMore={loadMore}
                hasMore={hasMore}
                loadingMore={loadingMore}
                onReply={handleReply}
                onReact={handleReact}
              />
              <MessageInput
                onSendText={handleSendText}
                onSendFile={handleSendFile}
                onSendAudio={handleSendAudio}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}